/**
 * Wire format of the replication endpoint and the parsers that validate it.
 *
 * The client pushes whole records it has changed and pulls every record the server has seen
 * since the client's cursor. Both directions carry full records, so the server never has to
 * reconstruct intermediate states and an op can be retried safely.
 */

import { err, ok, type Result } from './result.js';
import { isIsoDate } from './dates.js';
import {
  EntityKind,
  MemberRole,
  OverrideKind,
  isAttendanceState,
  isMemberRole,
  isOverrideKind,
  attendanceMarkId,
  type AttendanceMark,
  noteId,
  sessionOverrideId,
  type EntityMap,
  type EpochMillis,
  type Membership,
  type Note,
  type Person,
  type Project,
  type SessionOverride,
  type Weekday,
} from './types.js';

/** Monotonic server-assigned position in the replication log. `0` means "from the start". */
export type SyncCursor = number;

/** A record the client wants the server to accept. */
export type PushOp = {
  [K in EntityKind]: { kind: K; record: EntityMap[K] };
}[EntityKind];

/** Body of `POST /api/sync`. */
export interface SyncRequest {
  /** Highest cursor the client has already applied. */
  since: SyncCursor;
  /** Records changed locally since the last successful push, in causal order. */
  ops: PushOp[];
}

/** Records the server has accepted since the client's cursor, grouped by entity. */
export interface SyncChanges {
  projects: Project[];
  memberships: Membership[];
  people: Person[];
  sessionOverrides: SessionOverride[];
  attendanceMarks: AttendanceMark[];
  notes: Note[];
}

/** Why the server refused one pushed record. */
export enum RejectionReason {
  /** The signed-in user is not a member of the record's project. */
  NotAMember = 'not_a_member',
  /** The action requires the owner role. */
  NotOwner = 'not_owner',
  /** The record failed validation. */
  Malformed = 'malformed',
  /**
   * The record's key names a row that belongs to a different project.
   *
   * Authorization is checked against the project the record claims; this is the check that
   * the row the write would land on is in that same project.
   */
  WrongProject = 'wrong_project',
}

/** One refused push op, identified by entity kind and record id. */
export interface Rejection {
  kind: EntityKind;
  id: string;
  reason: RejectionReason;
}

/** Body of a successful `POST /api/sync` response. */
export interface SyncResponse {
  /** Cursor the client must send next time. */
  cursor: SyncCursor;
  /** Server wall clock, so the client can report drift. */
  serverTime: EpochMillis;
  changes: SyncChanges;
  /** Ops the server declined; the client drops them from its outbox and surfaces them. */
  rejections: Rejection[];
  /** The signed-in user's identity, echoed so the client can cache it for offline start-up. */
  identity: { userId: string; email: string };
}

/** Returns an empty change set. */
export function emptyChanges(): SyncChanges {
  return {
    projects: [],
    memberships: [],
    people: [],
    sessionOverrides: [],
    attendanceMarks: [],
    notes: [],
  };
}

/** Why a payload could not be parsed. */
export interface ParseError {
  /** Dotted path to the offending field, for example `ops.3.record.date`. */
  path: string;
  message: string;
}

/** Builds a parse failure at `path`. */
function fail<T>(path: string, message: string): Result<T, ParseError> {
  return err({ path, message });
}

/** Returns the value at `key` when `value` is a plain object, else `undefined`. */
function field(value: unknown, key: string): unknown {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>)[key] : undefined;
}

/** Parses a non-empty identifier string of at most 200 characters. */
function parseId(value: unknown, path: string): Result<string, ParseError> {
  if (typeof value !== 'string' || value.length === 0 || value.length > 200) {
    return fail(path, 'expected a non-empty id of at most 200 characters');
  }
  return ok(value);
}

/**
 * Parses an id that is used as a segment of a composite key.
 *
 * Composite keys join their parts with a colon, so a part that may itself contain one makes
 * the join ambiguous: two different records could then claim the same key, and the server's
 * check that a key belongs to its own project would pass for the wrong project. Forbidding
 * the separator in the parts makes every composite key decode to exactly one triple.
 *
 * Ids are generated as UUIDs, so nothing legitimate carries a colon.
 */
function parseKeySegment(value: unknown, path: string): Result<string, ParseError> {
  const parsed = parseId(value, path);
  if (!parsed.ok) return parsed;
  if (parsed.value.includes(':')) return fail(path, 'expected an id without a ":" separator');
  return ok(parsed.value);
}

/** Parses a display string, trimming it and enforcing a length ceiling. */
function parseText(value: unknown, path: string, maxLength: number): Result<string, ParseError> {
  if (typeof value !== 'string') return fail(path, 'expected a string');
  const trimmed = value.trim();
  if (trimmed.length > maxLength) return fail(path, `expected at most ${maxLength} characters`);
  return ok(trimmed);
}

/** Parses a finite, non-negative epoch-millisecond timestamp. */
function parseTimestamp(value: unknown, path: string): Result<EpochMillis, ParseError> {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return fail(path, 'expected a non-negative epoch-millisecond number');
  }
  return ok(value);
}

/** Parses a nullable timestamp, used for soft-delete markers. */
function parseNullableTimestamp(value: unknown, path: string): Result<EpochMillis | null, ParseError> {
  if (value === null) return ok(null);
  return parseTimestamp(value, path);
}

/** Parses a nullable `YYYY-MM-DD` day, used for open-ended term bounds. */
function parseNullableDate(value: unknown, path: string): Result<string | null, ParseError> {
  if (value === null) return ok(null);
  if (!isIsoDate(value)) return fail(path, 'expected null or a YYYY-MM-DD day');
  return ok(value);
}

/** Parses a de-duplicated, ascending list of weekday numbers. */
function parseWeekdays(value: unknown, path: string): Result<Weekday[], ParseError> {
  if (!Array.isArray(value)) return fail(path, 'expected an array of weekday numbers');
  const unique = new Set<number>();
  for (const entry of value) {
    if (typeof entry !== 'number' || !Number.isInteger(entry) || entry < 0 || entry > 6) {
      return fail(path, 'expected integers between 0 (Sunday) and 6 (Saturday)');
    }
    unique.add(entry);
  }
  return ok([...unique].sort((a, b) => a - b) as Weekday[]);
}

/** Parses the shared `updatedAt` / `deletedAt` pair. */
function parseSyncable(
  value: unknown,
  path: string,
): Result<{ updatedAt: EpochMillis; deletedAt: EpochMillis | null }, ParseError> {
  const updatedAt = parseTimestamp(field(value, 'updatedAt'), `${path}.updatedAt`);
  if (!updatedAt.ok) return updatedAt;
  const deletedAt = parseNullableTimestamp(field(value, 'deletedAt'), `${path}.deletedAt`);
  if (!deletedAt.ok) return deletedAt;
  return ok({ updatedAt: updatedAt.value, deletedAt: deletedAt.value });
}

const MAX_NAME_LENGTH = 120;
const MAX_NOTE_LENGTH = 500;
const MAX_EMAIL_LENGTH = 254;

/**
 * Ceiling on a session note.
 *
 * Longer than a person's roster note, because this is where what happened on a day is
 * written down, and short enough that a roster's worth of them still replicates on 2G. The
 * editor enforces the same limit, so a note that would be refused cannot be written.
 */
export const MAX_NOTE_TEXT_LENGTH = 2000;

/** Parses a project record from untrusted input. */
export function parseProject(value: unknown, path: string): Result<Project, ParseError> {
  const id = parseKeySegment(field(value, 'id'), `${path}.id`);
  if (!id.ok) return id;
  const name = parseText(field(value, 'name'), `${path}.name`, MAX_NAME_LENGTH);
  if (!name.ok) return name;
  const weekdays = parseWeekdays(field(value, 'weekdays'), `${path}.weekdays`);
  if (!weekdays.ok) return weekdays;
  const termStart = parseNullableDate(field(value, 'termStart'), `${path}.termStart`);
  if (!termStart.ok) return termStart;
  const termEnd = parseNullableDate(field(value, 'termEnd'), `${path}.termEnd`);
  if (!termEnd.ok) return termEnd;
  const syncable = parseSyncable(value, path);
  if (!syncable.ok) return syncable;
  // Projects written before `createdAt` existed fall back to their last write, which is the
  // earliest moment the record is known to have existed.
  const createdAtValue = field(value, 'createdAt');
  const createdAt = typeof createdAtValue === 'number' && Number.isFinite(createdAtValue) && createdAtValue >= 0
    ? createdAtValue
    : syncable.value.updatedAt;
  return ok({
    id: id.value,
    name: name.value,
    createdAt,
    weekdays: weekdays.value,
    termStart: termStart.value,
    termEnd: termEnd.value,
    countExcusedAsPresent: field(value, 'countExcusedAsPresent') === true,
    shareLinkPreview: field(value, 'shareLinkPreview') === true,
    ...syncable.value,
  });
}

/** Parses a membership record from untrusted input. */
export function parseMembership(value: unknown, path: string): Result<Membership, ParseError> {
  const id = parseId(field(value, 'id'), `${path}.id`);
  if (!id.ok) return id;
  const projectId = parseKeySegment(field(value, 'projectId'), `${path}.projectId`);
  if (!projectId.ok) return projectId;
  const email = parseText(field(value, 'email'), `${path}.email`, MAX_EMAIL_LENGTH);
  if (!email.ok) return email;
  if (!email.value.includes('@')) return fail(`${path}.email`, 'expected an email address');
  // The membership key embeds the address, so a colon in it would make the key ambiguous.
  // An address may only contain one inside a quoted local part, which no provider issues.
  if (email.value.includes(':')) return fail(`${path}.email`, 'expected an address without a ":"');
  const roleValue = field(value, 'role');
  if (!isMemberRole(roleValue)) return fail(`${path}.role`, 'expected "owner" or "member"');
  const userIdValue = field(value, 'userId');
  if (userIdValue !== null && typeof userIdValue !== 'string') {
    return fail(`${path}.userId`, 'expected null or a user id');
  }
  const syncable = parseSyncable(value, path);
  if (!syncable.ok) return syncable;
  return ok({
    id: id.value,
    projectId: projectId.value,
    email: email.value.toLowerCase(),
    userId: userIdValue as string | null,
    role: roleValue as MemberRole,
    ...syncable.value,
  });
}

/** Parses a person record from untrusted input. */
export function parsePerson(value: unknown, path: string): Result<Person, ParseError> {
  const id = parseKeySegment(field(value, 'id'), `${path}.id`);
  if (!id.ok) return id;
  const projectId = parseKeySegment(field(value, 'projectId'), `${path}.projectId`);
  if (!projectId.ok) return projectId;
  const name = parseText(field(value, 'name'), `${path}.name`, MAX_NAME_LENGTH);
  if (!name.ok) return name;
  const note = parseText(field(value, 'note'), `${path}.note`, MAX_NOTE_LENGTH);
  if (!note.ok) return note;
  const syncable = parseSyncable(value, path);
  if (!syncable.ok) return syncable;
  return ok({ id: id.value, projectId: projectId.value, name: name.value, note: note.value, ...syncable.value });
}

/** Parses a session-override record from untrusted input. */
export function parseSessionOverride(value: unknown, path: string): Result<SessionOverride, ParseError> {
  const id = parseId(field(value, 'id'), `${path}.id`);
  if (!id.ok) return id;
  const projectId = parseKeySegment(field(value, 'projectId'), `${path}.projectId`);
  if (!projectId.ok) return projectId;
  const date = field(value, 'date');
  if (!isIsoDate(date)) return fail(`${path}.date`, 'expected a YYYY-MM-DD day');
  const kind = field(value, 'kind');
  if (!isOverrideKind(kind)) return fail(`${path}.kind`, 'expected "added" or "skipped"');
  const syncable = parseSyncable(value, path);
  if (!syncable.ok) return syncable;
  return ok({
    id: id.value,
    projectId: projectId.value,
    date,
    kind: kind as OverrideKind,
    ...syncable.value,
  });
}

/** Parses an attendance mark from untrusted input. */
export function parseAttendanceMark(value: unknown, path: string): Result<AttendanceMark, ParseError> {
  const id = parseId(field(value, 'id'), `${path}.id`);
  if (!id.ok) return id;
  const projectId = parseKeySegment(field(value, 'projectId'), `${path}.projectId`);
  if (!projectId.ok) return projectId;
  const personId = parseKeySegment(field(value, 'personId'), `${path}.personId`);
  if (!personId.ok) return personId;
  const date = field(value, 'date');
  if (!isIsoDate(date)) return fail(`${path}.date`, 'expected a YYYY-MM-DD day');
  const state = field(value, 'state');
  if (!isAttendanceState(state)) return fail(`${path}.state`, 'expected an attendance state');
  const syncable = parseSyncable(value, path);
  if (!syncable.ok) return syncable;
  const author = field(value, 'updatedBy');
  return ok({
    id: id.value,
    projectId: projectId.value,
    personId: personId.value,
    date,
    state,
    // Marks written before authorship was recorded simply have no author.
    updatedBy: typeof author === 'string' && author.length <= MAX_EMAIL_LENGTH ? author.toLowerCase() : '',
    ...syncable.value,
  });
}

/**
 * Parses a note from untrusted input.
 *
 * The composite key is not checked here; the server checks it against the record's own
 * fields, which is the check that matters because it is what authorization is applied to.
 */
export function parseNote(value: unknown, path: string): Result<Note, ParseError> {
  const id = parseId(field(value, 'id'), `${path}.id`);
  if (!id.ok) return id;
  const projectId = parseKeySegment(field(value, 'projectId'), `${path}.projectId`);
  if (!projectId.ok) return projectId;
  const date = field(value, 'date');
  if (!isIsoDate(date)) return fail(`${path}.date`, 'expected a YYYY-MM-DD day');
  const personIdValue = field(value, 'personId');
  if (personIdValue !== null) {
    const personId = parseKeySegment(personIdValue, `${path}.personId`);
    if (!personId.ok) return personId;
  }
  const text = parseText(field(value, 'text'), `${path}.text`, MAX_NOTE_TEXT_LENGTH);
  if (!text.ok) return text;
  const syncable = parseSyncable(value, path);
  if (!syncable.ok) return syncable;
  const author = field(value, 'updatedBy');
  return ok({
    id: id.value,
    projectId: projectId.value,
    date,
    personId: personIdValue as string | null,
    text: text.value,
    updatedBy: typeof author === 'string' && author.length <= MAX_EMAIL_LENGTH ? author.toLowerCase() : '',
    ...syncable.value,
  });
}

/**
 * Returns true when a record's primary key is the one its own fields produce.
 *
 * Authorization is checked against a record's `projectId`, but the write lands on its `id`.
 * Unless the two are tied together, a caller who may write to any project at all can address
 * a row in a project they cannot see. These are that tie, for the three records whose key is
 * composed of fields the record carries.
 */
export function noteKeyMatches(note: Note): boolean {
  return note.id === noteId(note.projectId, note.date, note.personId);
}

/** Returns true when an override's id is the one its own project and date produce. */
export function overrideKeyMatches(override: SessionOverride): boolean {
  return override.id === sessionOverrideId(override.projectId, override.date);
}

/**
 * Returns true when a mark's id is the one its own person and date produce.
 *
 * A mark's key predates project scoping and names only the person and the day, so this alone
 * does not tie it to a project. The server pairs it with a check that the person named is on
 * the roster of the project the caller is authorized for.
 */
export function markKeyMatches(mark: AttendanceMark): boolean {
  return mark.id === attendanceMarkId(mark.personId, mark.date);
}

/** Largest number of records accepted in one push, bounding request size and work per call. */
export const MAX_OPS_PER_PUSH = 500;

/** Parses one push op, dispatching on its entity kind. */
function parsePushOp(value: unknown, path: string): Result<PushOp, ParseError> {
  const kind = field(value, 'kind');
  const record = field(value, 'record');
  switch (kind) {
    case EntityKind.Project: {
      const parsed = parseProject(record, `${path}.record`);
      return parsed.ok ? ok({ kind: EntityKind.Project, record: parsed.value }) : parsed;
    }
    case EntityKind.Membership: {
      const parsed = parseMembership(record, `${path}.record`);
      return parsed.ok ? ok({ kind: EntityKind.Membership, record: parsed.value }) : parsed;
    }
    case EntityKind.Person: {
      const parsed = parsePerson(record, `${path}.record`);
      return parsed.ok ? ok({ kind: EntityKind.Person, record: parsed.value }) : parsed;
    }
    case EntityKind.SessionOverride: {
      const parsed = parseSessionOverride(record, `${path}.record`);
      return parsed.ok ? ok({ kind: EntityKind.SessionOverride, record: parsed.value }) : parsed;
    }
    case EntityKind.AttendanceMark: {
      const parsed = parseAttendanceMark(record, `${path}.record`);
      return parsed.ok ? ok({ kind: EntityKind.AttendanceMark, record: parsed.value }) : parsed;
    }
    case EntityKind.Note: {
      const parsed = parseNote(record, `${path}.record`);
      return parsed.ok ? ok({ kind: EntityKind.Note, record: parsed.value }) : parsed;
    }
    default:
      return fail(`${path}.kind`, 'expected a known entity kind');
  }
}

/** Parses the body of `POST /api/sync` from untrusted input. */
export function parseSyncRequest(value: unknown): Result<SyncRequest, ParseError> {
  const since = field(value, 'since');
  if (typeof since !== 'number' || !Number.isInteger(since) || since < 0) {
    return fail('since', 'expected a non-negative integer cursor');
  }
  const rawOps = field(value, 'ops');
  if (!Array.isArray(rawOps)) return fail('ops', 'expected an array of push ops');
  if (rawOps.length > MAX_OPS_PER_PUSH) {
    return fail('ops', `expected at most ${MAX_OPS_PER_PUSH} ops per request`);
  }
  const ops: PushOp[] = [];
  for (const [index, rawOp] of rawOps.entries()) {
    const parsed = parsePushOp(rawOp, `ops.${index}`);
    if (!parsed.ok) return parsed;
    ops.push(parsed.value);
  }
  return ok({ since, ops });
}
